/**
 * @fileoverview The thin picture strip Today opens under: the season's painting, or the garden's own
 * photo when the gardener has chosen one (Profile, Display options;
 * TodayPictureSetting). Nothing sits over it: it had a pencil in its corner.
 *
 * The painting is the planner's season for this month in the gardener's
 * climate (early fall is the apple picking, late fall the watering under the
 * crows), cropped so the gardener in it is whole; see paintings. A photo of
 * the garden's own is centred. If the photo cannot be loaded, the painting
 * stands in rather than an empty frame.
 *
 * Where: Today, under any alert, above the date: a 4:1 strip since
 * 2026-10-09 (TODAY_STRIP_ASPECT), each painting framed so its gardener's
 * head and shoulders show (10:1 cut her out).
 * API: none; the day comes in (GET /today: painting or photo; painting.focus
 * is the server's crop for this strip).
 * Settings: the strip's crop is the server's (the Grow setting
 * today.STRIP_<key>, sent as painting.focus), else the Grow settings'
 * today.PAINTING_<key> (paintings).
 * Words (en/es): today.photo.alt (the garden's own photo only).
 *
 * @module TodayHeader
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { TodayDay } from '@/shared/api/today';
import { todayPicture } from '../lib/paintings';
import { TODAY_STRIP_ASPECT } from '@/config/tunables/today';

/** The day the picture belongs to. */
interface TodayHeaderProps {
  /** The server's day, or undefined while it loads. */
  day: TodayDay | undefined;
}

/**
 * The picture.
 *
 * @param props.day - The server's day, or undefined while it loads.
 */
export const TodayHeader: React.FC<TodayHeaderProps> = ({ day }) => {
  const { reactTranslate: t } = useTranslation();
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => setPhotoFailed(false), [day?.photo?.url]);

  const { src, position, ownPhoto } = todayPicture(day, photoFailed);
  // A painting framed on its gardener for the strip, as the server sends it (painting.focus); a garden photo centred.
  const focus = !ownPhoto && typeof day?.painting.focus === 'number' ? day.painting.focus : undefined;
  const framed = focus === undefined ? position : `center ${focus}%`;

  return (
    <div className="relative overflow-hidden rounded-md border bg-muted" style={{ aspectRatio: TODAY_STRIP_ASPECT }}>
      {src && (
        <img
          src={src}
          // The painting is decoration; the garden's own photo says what it is.
          alt={ownPhoto ? t('today.photo.alt') : ''}
          onError={() => { if (ownPhoto) setPhotoFailed(true); }}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: framed }}
        />
      )}
    </div>
  );
};
